import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, NotificationInline, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { CloseIcon } from 'assets/icons'
import { initiativeSettingsUrl, initiativeUrl } from 'config/constants/urls'
import { Input } from 'src/components/ConnectedForm'
import { useUpdateInitiative } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'

const InitiativeSettingsUrlEdit = ({ onCloseButtonClick }) => {
   const history = useHistory()
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()
   const hasGlobalUrl = initiative.globalUrls.length > 0

   const [globalUrlInputIsVisible, setGlobalUrlInputIsVisible] = useState(hasGlobalUrl)
   const [error, setError] = useState(null)
   const { showNotification } = useContext(NotificationManagerContext)

   const handleSaveInitiative = async (values, actions) => {
      setError(null)

      try {
         const result = await updateInitiative({ id: initiative.id }, {
            globalUrls: values.globalUrls?.trim() !== '' ? [values.globalUrls] : [],
            slug: values.slug
         })

         const updatedInitiative = result.data.updateInitiative

         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })

         // We assume that slug has been changed and update the route
         if (updatedInitiative.globalUrls.length) {
            window.location.assign(`/${updatedInitiative.globalUrls[0]}/settings`)
         } else {
            history.push(initiativeSettingsUrl(updatedInitiative))
         }
      } catch (err) {
         setError(err)
      } finally {
         actions.setSubmitting(false)
      }
   }

   return (
      <View width="100%" height="100%">
         <View
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom="1px solid #eee"
            height="54px"
            padding="0px 18px">
            <Text size="2" weight="medium">
               {`Edit ${initiative.type.nameSingular} URL`}
            </Text>
            <View
               display="flex"
               justifyContent="flex-end"
               alignItems="center"
               onClick={onCloseButtonClick}>
               <CloseIcon width="18px" height="18px" />
            </View>
         </View>
         <Formik
            initialValues={{
               globalUrls: initiative.globalUrls[0] || '',
               slug: initiative.slug
            }}
            onSubmit={handleSaveInitiative}
            render={({ handleSubmit, isSubmitting }) => (
               <Form
                  elementAppearance="plain"
                  elementBackgroundColor="#eee"
                  elementFocusBackgroundColor="#f3f3f3"
                  onSubmit={handleSubmit}>
                  <View
                     position="relative[a-b] absolute[c-f]"
                     width="100%"
                     height="calc(100% - 108px)"
                     scroll="on">
                     {error && (
                        <NotificationInline
                           paddingVertical="12px"
                           paddingHorizontal="18px"
                           textSize="1.5"
                           type="error"
                           title="Error"
                           message={error.graphQLErrors.map(e => e.message).join(', ') || error.message}
                        />
                     )}
                     <View width="100%" padding="18px">
                        <Text size="2" weight="medium">
                           {`Default ${initiative.type.nameSingular} URL`}
                        </Text>
                        <Text size="1.5" color="#999">
                           {`By default, this ${initiative.type.nameSingular} is accessible through the following URL:`}
                        </Text>
                        <Spacer size="2" />
                        <View
                           display="flex"
                           height="30px"
                           alignItems="center">
                           <View
                              flex="0 0 auto"
                              display="flex"
                              height="30px"
                              alignItems="center"
                              marginRight="12px">
                              <Text size="1.5" color="#999">
                                 {`domain.com${initiativeUrl(initiative).split('/').slice(0, -1).join('/')}/`}
                              </Text>
                           </View>
                           <View flex="1 1 auto">
                              <Input size="sm" name="slug" required />
                           </View>
                        </View>
                        <Spacer size="3" />
                        <View
                           display="flex"
                           width="100%"
                           borderTop="1px solid #eee"
                           paddingTop="18px">
                           <View flex="1 1 auto">
                              <Text size="2" weight="medium">
                                 Full Custom URL
                              </Text>
                              <Text size="1.5" color="#999">
                                 {`In addition to the default URL above, you can set a fully customized URL for this ${initiative.type.nameSingular}`}
                              </Text>
                              {!globalUrlInputIsVisible && (
                                 <Text size="1.5" color="#aaa">
                                    (i.e., yourdomain.com/custom)
                                 </Text>
                              )}
                           </View>
                           {!globalUrlInputIsVisible && (
                              <View flex="0 0 auto" paddingLeft="18px">
                                 <Button
                                    onClick={() => setGlobalUrlInputIsVisible(true)}
                                    name="Set"
                                    color="#eee"
                                    size="sm"
                                    textColor="#333"
                                 />
                              </View>
                           )}
                        </View>
                        <Spacer size="2" />
                        {globalUrlInputIsVisible && (
                           <View
                              display="flex"
                              height="30px"
                              alignItems="center">
                              <View
                                 flex="0 0 auto"
                                 display="flex"
                                 height="30px"
                                 alignItems="center"
                                 marginRight="12px">
                                 <Text size="1.5" color="#999">
                                    domain.com/
                                 </Text>
                              </View>
                              <View flex="1 1 auto">
                                 <Input
                                    size="sm"
                                    name="globalUrls"
                                    placeholder="custom"
                                 />
                              </View>
                           </View>
                        )}
                     </View>
                  </View>
                  <View
                     position="relative[a-b] absolute[c-f]"
                     bottom="0px"
                     left="0px"
                     width="100%"
                     display="flex"
                     justifyContent="flex-end"
                     alignItems="center"
                     borderTop="1px solid #eee"
                     height="54px"
                     padding="0px 12px">
                     <Button
                        type="submit"
                        name="Save Changes"
                        mode={isSubmitting ? 'loading' : 'normal'}
                     />
                  </View>
               </Form>
            )}
         />
      </View>
   )
}

InitiativeSettingsUrlEdit.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired
}

export default InitiativeSettingsUrlEdit
